import { useState, useRef, useMemo, useEffect, useCallback } from 'react';
import { Input, Box, Flex } from '@chakra-ui/react';
import { useField } from 'formik';
import { KText } from '@/ui-library/components';
import { XClose } from '@kula-ai/icons-core';
import { colorVar } from '@/theme/colorMode/colorVar';

interface TagInputProps {
  item?: Record<string, unknown>;
  field: any;
  formik?: any;
}

export const TagInput = ({ item = {}, field, formik }: TagInputProps) => {
  const {
    name,
    placeholder = 'Use Space or Enter for multiple keywords',
    styles,
    disabled,
    expanded = true,
    toggleExpandCollapse = () => {},
    showErrorStyles = false,
    isRequired,
  } = item as any;

  const [, meta, helpers] = useField(name);
  const { submitCount } = formik || {};
  const { touched } = meta || {};
  const { setValue } = helpers || {};
  const [inputValue, setInputValue] = useState('');
  const [showAll, setShowAll] = useState(expanded);
  const [maxVisibleTags, setMaxVisibleTags] = useState(3);
  const inputRef = useRef();

  const tags = useMemo(() => {
    return Array.isArray(field?.value) ? field.value : [];
  }, [field?.value]);

  // Keep local expand state in sync with prop updates.
  useEffect(() => {
    setShowAll(expanded);
  }, [expanded]);

  // Simple approach: calculate based on average tag length
  const calculateMaxVisibleTags = useCallback(() => {
    if (tags.length === 0) return 2;

    // Calculate average tag length
    const avgTagLength =
      tags.reduce((sum, tag) => sum + tag.length, 0) / tags.length;

    // Rough estimation: longer tags = show fewer, shorter tags = show more
    if (avgTagLength <= 3) return Math.min(tags.length, 4); // Short tags like "js", "go"
    if (avgTagLength <= 6) return Math.min(tags.length, 3); // Medium tags like "react", "python"
    if (avgTagLength <= 10) return Math.min(tags.length, 2); // Longer tags
    return Math.min(tags.length, 1); // Very long tags
  }, [tags]);

  // Update maxVisibleTags when tags change
  useEffect(() => {
    setMaxVisibleTags(calculateMaxVisibleTags());
  }, [tags, calculateMaxVisibleTags]);

  // Function to clean and split pasted values into tags
  const processValue = (value) => {
    if (!value || Array.isArray(value)) {
      return value;
    }

    // If it's a string, split by comma and space, clean up, and filter out empty strings
    const cleanedTags = value
      .split(/[,\s]+/) // Split by comma and/or spaces
      .map((tag) => tag.trim()) // Trim whitespace
      .filter((tag) => tag.length > 0); // Remove empty strings

    return cleanedTags;
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ' || e.key === ',') {
      e.preventDefault();
      if (inputValue.trim()) {
        const newTag = inputValue.trim();
        if (!tags.includes(newTag)) {
          const newTags = [...tags, newTag];
          if (setValue) {
            setValue(newTags);
          }
          if (formik?.setFieldValue) {
            formik.setFieldValue(name, newTags);
          }
        }
        setInputValue('');
      }
    } else if (e.key === 'Backspace' && !inputValue && tags.length > 0) {
      e.preventDefault();
      const newTags = tags.slice(0, -1);
      if (setValue) {
        setValue(newTags);
      }
      if (formik?.setFieldValue) {
        formik.setFieldValue(name, newTags);
      }
    }
  };

  const handleRemoveTag = (e, tagToRemove) => {
    e.preventDefault();
    e.stopPropagation();
    const newTags = tags.filter((tag) => tag !== tagToRemove);
    if (setValue) {
      setValue(newTags);
    }
    if (formik?.setFieldValue) {
      formik.setFieldValue(name, newTags);
    }
  };

  const handleInputChange = (e) => {
    setInputValue(e.target.value);
  };

  const handleBlur = () => {
    if (inputValue.trim() && !tags.includes(inputValue.trim())) {
      const newTags = [...tags, inputValue.trim()];
      if (setValue) {
        setValue(newTags);
      }
      if (formik?.setFieldValue) {
        formik.setFieldValue(name, newTags);
      }
    }
    setInputValue('');
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedText = e.clipboardData.getData('text');

    if (pastedText.trim()) {
      const processedTags = processValue(pastedText);

      if (processedTags.length > 0) {
        // Merge with existing tags, avoiding duplicates
        const newTags = Array.from(new Set([...tags, ...processedTags]));
        if (setValue) {
          setValue(newTags);
        }
        if (formik?.setFieldValue) {
          formik.setFieldValue(name, newTags);
        }
      }
    }
  };

  const shouldShowToggle = tags.length > maxVisibleTags;
  const displayTags = showAll ? tags : tags.slice(0, maxVisibleTags);
  const hiddenCount = tags.length - maxVisibleTags;

  const handleToggleShowAll = () => {
    setShowAll((prev) => !prev);
    toggleExpandCollapse();
  };

  const showError =
    (submitCount > 0 || touched) &&
    !showErrorStyles &&
    isRequired &&
    (!tags?.length || !!meta?.error);

  const isDisabled = !!disabled;

  return (
    <Flex
      w="100%"
      border="1px solid"
      borderColor={showError ? 'status-1' : 'secondary-100'}
      borderRadius="6px"
      minH="41px"
      p="4px 12px"
      flexWrap="wrap"
      alignItems="center"
      gap="4px"
      transition="background-color 0.15s ease, border-color 0.15s ease"
      bg={
        showError
          ? 'pastel-1-light'
          : isDisabled
            ? 'secondary-25'
            : 'base-white'
      }
      _hover={{
        borderColor: showError ? 'status-1' : 'secondary-300',
        bg: showError
          ? 'pastel-1-light'
          : isDisabled
            ? 'secondary-25'
            : 'secondary-25',
        boxShadow: 'none',
      }}
      _focusWithin={{
        borderColor: showError ? 'status-1' : 'secondary-300',
        bg: showError ? 'pastel-1-light' : 'secondary-25',
        boxShadow: 'none',
      }}
    >
      <Flex flexWrap="wrap" alignItems="center" gap="4px">
        {displayTags.map((tag, index) => (
          <Flex
            key={index}
            alignItems="center"
            gap="4px"
            border="1px solid"
            borderColor="border-color"
            borderRadius="6px"
            p="2px 6px"
          >
            <KText mt="-4px">{tag}</KText>
            <Box cursor="pointer" onClick={(e) => handleRemoveTag(e, tag)}>
              <XClose size={14} className="kula-icon" />
            </Box>
          </Flex>
        ))}

        {!showAll && shouldShowToggle && (
          <Flex
            cursor="pointer"
            onClick={handleToggleShowAll}
            border="1px solid"
            borderColor={colorVar('border-color')}
            borderRadius="6px"
            p="3px 6px"
            alignItems="center"
            justifyContent="center"
          >
            <KText color="primary-500">+{hiddenCount}</KText>
          </Flex>
        )}
      </Flex>

      <Input
        ref={inputRef}
        value={inputValue}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        onPaste={handlePaste}
        onBlur={handleBlur}
        variant="unstyled"
        autoComplete="off"
        flex="1"
        minW="120px"
        _placeholder={{ color: 'secondary-500' }}
        placeholder={tags.length === 0 ? placeholder : ''}
        disabled={disabled}
        {...styles}
      />
    </Flex>
  );
};
